import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Spinner, Text } from "@fluentui/react-components";
import { ENTITY_CANDIDATE, ENTITY_RELATION, ENTITY_RELATION_CONTACT } from "../../../utils/Constants";
import { useMessage } from "../../../blocks/message/MessageContext";
import { documentFormFields } from "./DocumentFormFields";
import PageHeader from "../../../blocks/PageHeader";
import Ows from "../../../utils/OwsHandler";
import Form from "../../form/Form";
import { allowedContentType } from "../../../utils/Helpers";

const DocumentForm = () => {
  const { showMessage } = useMessage();
  const [alwaysOnTop, setAlwaysOnTop] = useState(false);
  const [documentTypes, setDocumentTypes] = useState({});
  const [loading, setLoading] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const { uid, entity, document } = location?.state || {};

  useEffect(() => {
    (async () => {
      try {
        const data = await Ows.handleRequest("Otys.Services.NoticeService.getTypesForEntity", [
          entity, 
          "Document"
        ]);
        
        if (data.error) {
          throw new Error(data.error.message);
        }
        
        setDocumentTypes(data.response || {});
      } catch (error) {
        console.error(error);
      }
    })();
  }, [entity]);

  const createDocument = async (doc) => {
    try {
      const data = await Ows.handleRequest("Otys.Services.FileContentService.save", [
        doc.name, 
        doc.content
      ]);

      if (data.error) {
        throw new Error(data.error.message);
      };

      return data.response.ouid;
    } catch (error) {
      console.error(error);
    }
  };

  const saveDocument = async (docOuid, formData) => {
    setLoading(true);
    try {
      const entityUid = {};
      switch (entity) {
        case ENTITY_CANDIDATE:
          entityUid["candidateUid"] = uid;
          break;
        case ENTITY_RELATION:
          entityUid["relationUid"] = uid;
          break;
        case ENTITY_RELATION_CONTACT:
          entityUid["relationContactUid"] = uid;
          break;
        default:
          showMessage("error", "Invalid entity.");
      }

      const data = await Ows.handleRequest("Otys.Services.AttachedDocumentsService.add", [
        {
          ...entityUid,
          alwaysOnTop: formData.alwaysOnTop,
          description: formData.description,
          subject: formData.title,
          typeId: formData.type,
          fileUid: docOuid,
        }
      ]);
      
      if (data.error) {
        throw new Error(data.error.message);
      }

      return data.response;
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (formData) => {
    const allowedDocType = allowedContentType(document.contentType);

    if (!allowedDocType) {
      showMessage("error", "Invalid document type. Allowed types are PDF, DOC, DOCX");
      return;
    }

    const docOuid = await createDocument(document);

    if (!docOuid) {
      showMessage("error", "Failed to create document.");
      return;
    }

    const doc = await saveDocument(docOuid, formData);

    if (!doc) {
      showMessage("error", "Failed to create document.");
    } else {
      showMessage("success", "Document created successfully.");
      handleBack();
    }
  };

  const handleBack = () => {
    navigate(-1);
  }

  return (
    <div>
      <PageHeader onBack={handleBack} />
      <div style={{ padding: "20px" }}>
        {loading ? <Spinner /> : 
          <>
            <Form title="Upload document" fields={documentFormFields(documentTypes, alwaysOnTop, setAlwaysOnTop)} onSubmit={handleSubmit} />
            {document && (
              <div>
                <Text as="p" size={300} weight="semibold" block>
                  Document: {document.name}
                </Text>
              </div>
            )}
          </>
        }
      </div>
    </div>
  );
};

export default DocumentForm;
